import * as React from 'react';
import { connect } from 'react-redux';

import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';
import InspirationalMaterialsImageUpload from '../inpirational-materials-image-upload/InspirationalMaterialsImageUpload';

import { addMaterialLinks, uploadAssets } from '../../reducers/inspirationalMaterials';
import { danger } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { isStrictInstagramReelUrl, isTikTokVideoUrl, isYoutubeUrl } from 'utils/url';
import { MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_IN_MB, MAX_VIDEO_SIZE, MAX_VIDEO_SIZE_IN_MB } from 'utils/campaignAsset';

import type { Dispatch } from 'types';

import './inspirational-materials-upload.scss';

applyTranslations(require('./inspirational-materials-upload.i18n.json'));

type DispatchProps = {
  dispatchAddLink: (a: string) => Promise<void>,
  dispatchUploadAssets: (assets: Array<File>) => void,
  dispatch: Dispatch
};

type Props = DispatchProps;

const INITIAL_STATE = {
  inputValue: '',
  isError: false
};

const InspirationalMaterialsUpload = (
  {
    dispatchAddLink,
    dispatchUploadAssets,
    dispatch
  }: Props
): JSX.Element => {
  const [state, setState] = React.useState(INITIAL_STATE);

  const handleAddLink = () => {
    const { inputValue } = state;

    if (!(isTikTokVideoUrl(inputValue) || isYoutubeUrl(inputValue) || isStrictInstagramReelUrl(inputValue))) {
      setState({ ...state, isError: true });
      return;
    }

    dispatchAddLink(inputValue);
    setState(INITIAL_STATE);
  };

  const uploadInspirationalAssets = (assets: Array<File>) => {
    let isVideoLimitExceeded = false;
    let isImagePdfLimitExceeded = false;
    const modifiedAssets: Array<File> = [];

    for (let i = 0; i < assets.length; i++) {
      const file = assets[i];
      const isImagePdf = file.type.includes('image') || file.type.includes('pdf');
      const isVideo = file.type.includes('video');

      if (isVideo && file.size > MAX_VIDEO_SIZE) {
        isVideoLimitExceeded = true;
        continue;
      }

      if (isImagePdf && file.size > MAX_IMAGE_SIZE) {
        isImagePdfLimitExceeded = true;
        continue;
      }

      if (isImagePdf || isVideo) {
        modifiedAssets.push(file);
      }
    }

    if (isVideoLimitExceeded || isImagePdfLimitExceeded) {
      dispatch(danger(__t('inspirational_materials_upload.inspirational_material_max_asset_size_message', { MAX_IMAGE_SIZE_IN_MB, MAX_VIDEO_SIZE_IN_MB })));
    }

    dispatchUploadAssets(modifiedAssets);
  };

  const { inputValue, isError } = state;

  return (
    <section className="inspirational-materials-upload">
      <InspirationalMaterialsImageUpload uploadAssets={uploadInspirationalAssets} />
      <section className="inspirational-materials-upload__link-input">
        <Input
          className="inspirational-materials-upload__input"
          value={inputValue}
          placeholder={__t('inspirational_materials_upload.add_link_placeholder')}
          onChange={(evt) => setState({ inputValue: evt.currentTarget?.value || '', isError: false })}
        />
        <ActionButton
          color="tertiary"
          disabled={!inputValue}
          onClick={handleAddLink}
        >
          {__t('inspirational_materials_upload.add_link_label')}
        </ActionButton>
        {
          isError && (
            <div className="inspirational-materials-upload__input-error-message">
              {__t('inspirational_materials_upload.link_error_message')}
            </div>
          )
        }
      </section>
    </section>
  );
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchAddLink: (link: string) => dispatch(addMaterialLinks([link])),
  dispatchUploadAssets: (assets: Array<File>) => dispatch(uploadAssets(assets)),
  dispatch
});

export default connect(null, mapDispatchToProps)(InspirationalMaterialsUpload);
